/**
 * iTrace Documentation Guides
 *
 * Each guide is a small content object describing a single task a user can perform.
 * The Documentation page renders these as searchable, step-by-step articles.
 *
 * Fields:
 *   id          — url-safe unique slug
 *   title       — short human-readable title
 *   summary     — one-line teaser shown in the library index + used for search
 *   tags        — keyword array used by the search input
 *   category    — grouping label ("Setup", "Cycle", "iTrace", …)
 *   sections    — ordered list of { heading, description?, steps?, note?, image? }
 *                 steps[i] may reference { annotation, image, hook } so the article
 *                 shows a callout + screenshot + on-page selector the user can inspect.
 *
 * Screenshots live under /public/docs/itrace/ and are captured via Playwright.
 */
const itraceGuides = [
  {
    id: 'whats-new',
    title: "What's new — In-app QR Viewer & PDF export",
    summary: 'SIRIM can now render and print the actual QR codes without opening the iTrace portal, and every guide is exportable as PDF.',
    tags: ['qr viewer', 'pdf', 'print', 'new', 'whats new', 'update', 'feature'],
    category: 'Features',
    sections: [
      {
        heading: '1. In-app QR Viewer',
        description:
          'Sidebar → iTraceability → on any completed cycle row click the green "View QR Codes" button. A modal opens showing every scannable QR image for that cycle, fetched live from iTrace and rendered with qrcode.react. No portal round-trip needed.',
        hook: 'Completed Cycles table → "View QR Codes" column',
        image: '/docs/itrace/qr-viewer-in-app.png',
      },
      {
        heading: '2. Per-QR copy and PNG download',
        description:
          'Each QR card has a clipboard icon (copies the trace URL) and a download icon (saves that specific QR as a 400×400 PNG). Use this when you need to share one label digitally.',
      },
      {
        heading: '3. Print the whole commission / Save as PDF',
        description:
          'In the QR Viewer toolbar click "Print / Save as PDF". The browser\'s print dialog opens. Choose "Save as PDF" as the destination. You get a single PDF with every QR code in a clean grid layout — ready to email to a contract printer.',
        note:
          'Our print stylesheet auto-hides the sidebar, header, and interactive buttons when printing, so the PDF is clean.',
      },
      {
        heading: '4. Print any documentation guide as PDF',
        description:
          'The same Print / Save as PDF button is also at the top-right of every guide in this library. Use it to archive procedures offline, or share SOPs with staff who don\'t have a login.',
        hook: 'Guide detail page → "Print / Save as PDF" (top-right)',
        image: '/docs/itrace/docs-print-button.png',
      },
      {
        heading: '5. Smart empty states',
        description:
          'The QR Viewer tells you exactly what is missing: "No Plant Batch" (re-end the cycle), "Plant Batch exists but no Commission" (open iTrace to create one), or the full QR grid when everything is ready. No guessing.',
      },
    ],
  },

  {
    id: 'register-farm',
    title: 'Register a new Farm',
    summary: 'Create a Farm record in SIRIM before you can plan cycles or send harvests to iTrace.',
    tags: ['farm', 'register', 'setup', 'create farm', 'new farm'],
    category: 'Setup',
    sections: [
      {
        heading: '1. Open the Farm setup wizard',
        description:
          'After signing up, Kapitani takes you through a 3-step onboarding wizard: Account → Farm → Cycle. You can also open it directly at /setup/farm.',
        hook: 'Sidebar → Farm Management, or direct URL /setup/farm',
        image: '/docs/itrace/register-farm.png',
      },
      {
        heading: '2. Fill in farm details',
        description:
          'Enter Farm Name, Farm Size (hectares), Address (Line 1 is required), City, Postcode, and State. These fields are also forwarded to iTrace as part of the Plant Batch description when you end a cycle.',
        note:
          'Tip: use "Pick on Map" to drop a pin instead of typing coordinates manually. The latitude/longitude auto-populate.',
        image: '/docs/itrace/register-farm.png',
      },
      {
        heading: '3. Continue to Cycle Setup',
        description:
          'Click "Continue to Cycle Setup →" to move on. The new farm is created in Supabase and the wizard advances to step 3 (Cycle).',
        hook: 'Bottom of the Farm form — green "Continue to Cycle Setup →" button',
        image: '/docs/itrace/register-farm.png',
      },
    ],
  },

  {
    id: 'create-cycle',
    title: 'Create a Cycle (Season / Farm Diary)',
    summary: 'Start a new growing cycle inside a farm. A cycle is what eventually becomes a harvest and a Plant Batch in iTrace.',
    tags: ['cycle', 'season', 'farm diary', 'planting', 'start cycle', 'new cycle'],
    category: 'Cycle',
    sections: [
      {
        heading: '1. Open the farm',
        description:
          'Sidebar → Farm Management → pick the farm from the "Current Farm" dropdown. You should land on the Farm Overview tab.',
        hook: 'Sidebar → Farm Management',
        image: '/docs/itrace/farm-management-overview.png',
      },
      {
        heading: '2. Click the "Add Produce" tab',
        description:
          'Between Farm Overview and Farm Diary is an "Add Produce" tab. Clicking it opens the 2-step wizard (Select Produce → Schedule Crop).',
        hook: 'Tab bar: Farm Overview · Add Produce · Farm Diary',
        image: '/docs/itrace/add-produce-cycle.png',
      },
      {
        heading: '3. Select the produce',
        description:
          'Type the produce name (e.g. "Lettuce", "Sawit", "Nenas") in the search box, then pick the match. The produce you select is what will appear in the iTrace batch name later, so pick the real crop.',
        note:
          'The produce list comes from the `produce_name` table in Supabase. If a crop is missing, ask an admin to add it.',
        image: '/docs/itrace/add-produce-cycle.png',
      },
      {
        heading: '4. Schedule the crop',
        description:
          'Click "Next: Schedule Crop" and fill in the planting date, expected harvest window, and expected yield. These become the cycle parameters.',
      },
      {
        heading: '5. Save',
        description:
          'The new cycle now appears in the Farm Diary tab with status "active". It is ready to receive activities and eventually be ended.',
        hook: 'Farm Diary tab — look for the new cycle card with an "active" badge',
      },
    ],
  },

  {
    id: 'end-cycle-send-itrace',
    title: 'End a Cycle and send harvest to iTrace',
    summary: 'Mark a cycle as complete. SIRIM creates a Plant Batch in iTrace and gives you a portal link for QR generation.',
    tags: ['end cycle', 'harvest', 'send to itrace', 'plant batch', 'complete'],
    category: 'Cycle',
    sections: [
      {
        heading: '1. Open the cycle inside Farm Diary',
        description:
          'Farm Management → Farm Diary tab. Active cycles show a green "active" badge with a progress bar.',
        hook: 'Farm Management → Farm Diary',
        image: '/docs/itrace/farm-management-end-cycle.png',
      },
      {
        heading: '2. Scroll to the cycle card and click "End Cycle"',
        description:
          'The End Cycle button is the green button on the right of the cycle card action row, next to Add Activity and Update Progress.',
        hook: 'Cycle card → right-most green button "End Cycle"',
        image: '/docs/itrace/cycle-end-cycle-button.png',
      },
      {
        heading: '3. Fill the End Cycle form',
        description:
          'Enter the Actual Harvest Date (defaults to today), Actual Yield in the cycle\'s unit, and any optional Completion Notes. The yellow note explains what the flow does.',
        image: '/docs/itrace/end-cycle-modal.png',
      },
      {
        heading: '4. Click "Complete Cycle & Send to iTrace"',
        description:
          'SIRIM does three things: (1) updates the season in Supabase to mark it ended, (2) authenticates with iTrace, (3) creates a Plant Batch. The button shows "Sending to iTrace..." while this runs (~2-3 seconds).',
        note:
          'If the iTrace call fails, the error is shown in red inside the modal. The cycle update may have succeeded in Supabase even if iTrace fails — retry via the iTraceability page.',
      },
      {
        heading: '5. Plant Batch confirmation',
        description:
          'A modal confirms the Plant Batch details: Batch Name (auto-generated from produce + date), EPC code (globally unique), Batch ID (iTrace internal), Site ID, and Status (Available). These are the identifiers of this batch inside iTrace.',
        image: '/docs/itrace/plant-batch-created.png',
      },
      {
        heading: '6. Click "Open iTrace Portal"',
        description:
          'This opens the Serialization portal in a new tab with a one-time service-token URL that auto-logs you in as Sirim Site Admin. Use this to create a Commission (the step that actually generates QR labels).',
        note:
          'Service tokens are single-use. If you close the tab and come back later, just click "Open iTrace Portal" again — a new token is generated each call.',
        image: '/docs/itrace/itrace-dashboard.png',
      },
      {
        heading: '7. After the commission is created, view QR codes right inside SIRIM',
        description:
          'Back in SIRIM → Sidebar → iTraceability → find this cycle in the Completed Cycles table → click "View QR Codes". No need to return to the iTrace portal to see your QRs.',
        hook: 'Sidebar → iTraceability → Completed Cycles → "View QR Codes"',
        image: '/docs/itrace/qr-viewer-in-app.png',
      },
    ],
  },

  {
    id: 'generate-qr-commission',
    title: 'Generate QR codes in iTrace (Create a Commission)',
    summary: 'Inside the iTrace portal, link your Plant Batch to a Commission and generate printable QR labels.',
    tags: ['commission', 'qr', 'qr code', 'generate', 'labels', 'print', 'serialization'],
    category: 'iTrace',
    sections: [
      {
        heading: '1. Land on the iTrace dashboard',
        description:
          'After clicking "Open iTrace Portal" in SIRIM, you land here, auto-logged in as Sirim Site Admin (Site: SIRIM). The dashboard shows Total Commissions and the three modules you have access to.',
        image: '/docs/itrace/itrace-dashboard.png',
      },
      {
        heading: '2. Open Commission Tasks',
        description:
          'Click "Commission Tasks" (the first module card) or use the rail nav: Commission → Commission Tasks. You will see a list of all existing commissions with status, label count, printed count, and verified count.',
        image: '/docs/itrace/itrace-commission-tasks.png',
      },
      {
        heading: '3. Click "New Commission"',
        description:
          'Top-right button. This opens a 4-step form.',
        hook: 'Commission Tasks page → "+ New Commission" button',
        image: '/docs/itrace/new-commission-form.png',
      },
      {
        heading: '4. Step 1 — Commission Info',
        description:
          'Give the commission a name (or leave blank to auto-generate) and specify the Number of Labels. This is how many QR codes will be generated — one per physical product unit.',
        note:
          'Example: "Sawit Harvest — SIRIM Test 10 Labels" with 10 labels will produce 10 unique trace URLs.',
        image: '/docs/itrace/commission-step1-info.png',
      },
      {
        heading: '5. Step 2 — Pick a Product',
        description:
          'Type part of the product name in the search box (e.g. "Lettuce"). Pick the product from the dropdown. The product must already exist in the iTrace catalog — if not, ask an iTrace admin to add it.',
        image: '/docs/itrace/commission-step2-product.png',
      },
      {
        heading: '5a. Product selected',
        description:
          'Once selected, the product appears in the field with an X button to clear it. You can change it by clicking X and searching again.',
        image: '/docs/itrace/commission-step2-product-selected.png',
      },
      {
        heading: '6. Step 3 — Manufacturing Details',
        description:
          'Fill Product Type (default: Food & Beverage), Net Weight in grams (default: 100g), Production Date (today), and Expiration Date (today + 14 days). These are printed on each label and used for consumer-side traceability.',
        image: '/docs/itrace/commission-step3-manufacturing.png',
      },
      {
        heading: '7. Step 4 — Link the Plant Batch',
        description:
          'Leave "Plant Batch" selected (Recommended). In the search box type part of the batch name (e.g. "Sawit" or "Lettuce 24/4"). The list shows all available batches; pick the one SIRIM just created for this harvest.',
        note:
          'Only Plant Batches with status "Available" appear here. Batches already linked to another commission show as "Assigned" and are hidden.',
        image: '/docs/itrace/commission-step4-batch-source.png',
      },
      {
        heading: '7a. Plant Batch linked',
        description:
          'Once picked, a green confirmation card shows the selected batch with a leaf icon and the EPC code.',
        image: '/docs/itrace/commission-step4-batch-selected.png',
      },
      {
        heading: '8. Click "Create Commission"',
        description:
          'iTrace generates the labels (each one gets a unique serial and a unique trace URL) and returns you to the Commission Tasks list. The new commission shows with status CREATED and Unprinted = Label Count.',
        image: '/docs/itrace/commission-created.png',
      },
      {
        heading: '9. View the generated labels',
        description:
          'Click the commission card to open its detail page. Click "10 Label Codes" (or whatever count) to see each unique trace URL. Every URL is the payload of one QR code.',
        image: '/docs/itrace/commission-labels-sawit.png',
      },
      {
        heading: '10. Example — what a QR code looks like',
        description:
          'Below is a real scannable QR code from this commission. It encodes the trace URL https://pwa-qrtrace-dev.web.app/c/33I97TC8TG — scanning it takes a consumer to the product trace page. Each of the 10 labels has its own unique URL like this.',
        image: '/docs/itrace/qr-example-sawit.png',
      },
      {
        heading: '11. Example — the printed product label',
        description:
          'When printed, the QR is placed on a SIRIM-branded label like this. It carries the iTrace, VegeTrace, and SIRIM logos, product name, net weight, barcode, and Halal mark.',
        image: '/docs/itrace/pack-media-lettuce.jpg',
      },
      {
        heading: '12. Print or Download',
        description:
          'From the commission detail page use "Print Labels" with a Quantity to send to the configured printer (Epson C6050P, 101×152mm glossy labels). Alternatively click "Download" (CSV / XLS / XLSX) on the commission card to export the full list of serials and trace URLs.',
      },
      {
        heading: '13. Or use the in-app QR Viewer (recommended for digital use)',
        description:
          'Back in SIRIM → Sidebar → iTraceability → the cycle you just commissioned will have a green "View QR Codes" button. Open it to see every QR as a live scannable image, copy individual trace URLs, download single PNGs, or print the whole grid as a PDF.',
        hook: 'Sidebar → iTraceability → Completed Cycles → "View QR Codes"',
        image: '/docs/itrace/qr-viewer-in-app.png',
      },
    ],
  },

  {
    id: 'view-existing-qr',
    title: 'View an existing QR code or commission',
    summary: 'View the actual QR code images for any completed cycle directly inside SIRIM, or re-open the iTrace portal for print / CSV export.',
    tags: ['view qr', 'reprint', 'find commission', 'download', 'existing', 'qr viewer', 'pdf'],
    category: 'iTrace',
    sections: [
      {
        heading: '1. Go to iTraceability',
        description:
          'Sidebar → iTraceability. The page lists all completed cycles for farms you can see.',
        image: '/docs/itrace/itraceability-page.png',
      },
      {
        heading: '2. Click "View QR Codes" on the cycle row',
        description:
          'Each completed cycle now has a "View QR Codes" action (green eye icon, left of "Open in iTrace"). This opens an in-app QR Viewer modal — no need to jump to the iTrace portal just to see the codes.',
        hook: 'Completed Cycles table → Actions column → "View QR Codes"',
        image: '/docs/itrace/itraceability-with-real-cycles.png',
      },
      {
        heading: '3. What the QR Viewer does',
        description:
          'SIRIM looks up the Plant Batch linked to this cycle (match by "Cycle: <id>" in the batch description), finds the Commission linked to that batch, fetches every label trace code, and renders each as a live scannable QR image. All client-side — you can scan any QR straight off the screen with your phone.',
        note:
          'If the cycle has no Plant Batch yet, or a Plant Batch but no Commission, the modal tells you what step to do next. For example, "Plant Batch exists but no Commission linked — open iTrace to create one".',
      },
      {
        heading: '4. Save a single QR as a PNG',
        description:
          'Each QR card has a download icon. Click it to save that specific QR as a 400×400 PNG file. Useful when you need to send just one label digitally.',
      },
      {
        heading: '5. Save all QR codes as a PDF (or print them)',
        description:
          'Click "Print / Save as PDF" in the viewer toolbar. The browser\'s print dialog opens and sidebar / buttons / menus are auto-hidden by our print stylesheet. Choose "Save as PDF" as the destination to get a single PDF containing every QR in a clean grid layout — great for emailing labels to a contract printer.',
        hook: 'QR Viewer toolbar → "Print / Save as PDF"',
      },
      {
        heading: '6. Copy a single trace URL',
        description:
          'Each QR card also has a clipboard icon. Click to copy the full trace URL (e.g. https://pwa-qrtrace-dev.web.app/c/33I97TC8TG) to your clipboard.',
      },
      {
        heading: '7. Need to reprint from a physical printer?',
        description:
          'The in-app viewer covers viewing and PDF export. For physical label printing (Epson C6050P, 101×152mm glossy), click "Open in iTrace Portal" in the viewer toolbar, go to the commission detail page, and use "Print Labels" there — that pushes the job straight to the networked printer.',
        image: '/docs/itrace/commission-details.png',
      },
      {
        heading: '8. Original iTrace portal view (still available)',
        description:
          'If you prefer the iTrace portal\'s built-in Label Codes panel (a text list of trace URLs with CREATED / PRINTED / VERIFIED status and pagination), click "Open in iTrace" on the cycle row. The side panel shown below is how it looks inside iTrace.',
        image: '/docs/itrace/label-codes-list.png',
      },
    ],
  },

  {
    id: 'itrace-end-to-end-workflow',
    title: 'iTrace end-to-end workflow (SIRIM + portal)',
    summary: 'The complete flow from ending a SIRIM cycle to live consumer-scannable QR codes. Shows exactly which steps SIRIM automates and which require the iTrace portal.',
    tags: ['itrace', 'workflow', 'end to end', 'commission', 'plant batch', 'validate', 'serialization module', 'pda', 'scan'],
    category: 'iTrace',
    sections: [
      {
        heading: '0. Prerequisites (one-time, done by Kapitani / iTrace team)',
        description:
          'Before any of this works, the iTrace admin must have configured your Company (SIRIM), a Read Point (e.g. "Farm PDA") with GPS + role flags, a Product (e.g. "Green Coral Lettuce Pack") and a matching EPC Class. These live in the iTrace admin portal at ufarm-dev.itrace.app. If a produce type you want to trace is missing a Product/EPC Class, contact the iTrace team (Eason) before continuing.',
      },
      {
        heading: '1. End the cycle in SIRIM',
        description:
          'Sidebar → Farm Management → open the active cycle → "End Cycle". Fill the actual harvest date, yield, and any notes. Click "End Cycle".',
        hook: 'Farm Management → cycle card → "End Cycle"',
      },
      {
        heading: '2. SIRIM creates a Plant Batch in iTrace (automatic)',
        description:
          'Behind the scenes SIRIM calls the iTrace Serialization API: get access token → POST /serialization/api/plant-batches with the batch name and description. A confirmation modal opens showing the new Plant Batch (EPC code, batch ID, status).',
      },
      {
        heading: '3. Open the iTrace portal from the confirmation modal',
        description:
          'Click "Open iTrace Portal". SIRIM generates a single-use service token and opens the Serialization module in a new tab with you already logged in as sirimSiteAdmin@gmail.com. Tokens are one-shot — if the tab reloads or the session expires, click the button again.',
      },
      {
        heading: '4. Configure Business Location + Readpoint (one-time)',
        description:
          'First time you use the Serialization module: click More → App Settings → Business Location (pick SIRIM) → Readpoint (pick Farm PDA or whichever applies) → Save Settings. Skip this on subsequent visits.',
      },
      {
        heading: '5. Create a Commission for the Plant Batch',
        description:
          'In the Serialization module: sidebar → Commission Tasks → "New Commission". Fill: commission name, Number of Labels (e.g. 30), pick the Product, then in Batch Source search for and pick the Plant Batch SIRIM just created (its EPC code appears in the SIRIM modal you came from). Click "Create Commission". This is the step that generates the actual QR labels.',
        note:
          'Without this commission, no QR codes exist for this harvest — the SIRIM "View QR Codes" button will show "Plant Batch exists but no Commission linked yet". One Plant Batch can only ever map to one Commission.',
      },
      {
        heading: '6. Print the labels',
        description:
          'On the Commission detail page, click "Print Labels". Labels move from status CREATED → PRINTED. Use the configured printer (Network printer, Standard Glossy Label 101×152mm).',
      },
      {
        heading: '7. Physically scan + validate (the critical step)',
        description:
          'After printing: each label must be physically scanned with the iTrace PDA app (or by pasting trace codes into the "Scan or Manual Input Code" field on the Commission page). Press Enter after each scan. When all labels are scanned, click "Validate Commission". Status moves PRINTED → VERIFIED. Only after this step do consumer scans of the QR resolve to product info — until then, scanning the URL shows "102 product not found".',
        note:
          'This is the SOP step 36-45 in the Scribe "Serialization Module Guideline". The pwa-qrtrace-dev.web.app/c/<code> URL only goes live for codes that reached VERIFIED status.',
      },
      {
        heading: '8. View the QR codes from SIRIM',
        description:
          'Back in SIRIM: sidebar → iTraceability → on the completed cycle row click the green "View QR Codes" button. The modal pulls the commission and renders every QR inline with status badges. You can copy any URL, download a single QR as PNG, or print/save the whole set as a PDF.',
        hook: 'Sidebar → iTraceability → completed cycle → "View QR Codes"',
      },
      {
        heading: '9. (Optional) Consumer-side verification',
        description:
          'A consumer scans the printed QR → opens https://pwa-qrtrace-dev.web.app/c/<trace_code> → clicks "Trace Product" → sees product info, production date, expiration date, and the commissioning event. If status is still CREATED or PRINTED they will see "102 product not found" — go back and finish step 7.',
      },
      {
        heading: '10. What might one day replace steps 5–7 (middleware path)',
        description:
          'The Kapitani middleware V2 spec defines APIs 11/12 (POST/GET /client/{clientId}/harvest) that would let SIRIM hand the whole commission + label flow to middleware, which would call iTrace internally and return the QR code directly. That endpoint is NOT deployed today (verified 2026-05-18 in middleware admin). If/when Kapitani registers the harvest service under the iTrace vendor + subscribes SIRIM, the End Cycle confirmation modal in SIRIM will automatically start showing the qrCode inline — no portal trip required.',
      },
    ],
  },

  {
    id: 'troubleshooting',
    title: 'Troubleshooting iTrace integration',
    summary: 'Common iTrace errors and how to fix them.',
    tags: ['troubleshoot', 'error', 'forbidden', 'invalid_request_source', 'auth failed', 'fix'],
    category: 'iTrace',
    sections: [
      {
        heading: 'Plant Batch creation failed (403 / forbidden / invalid_request_source)',
        description:
          'This was the old endpoint on port 8082. The current working endpoint is POST http://190.92.203.168:8080/serialization/api/plant-batches. If you see this error the service file in SIRIM is pointing at the wrong URL — check src/services/itraceSerializationService.js.',
      },
      {
        heading: 'iTrace auth failed (401)',
        description:
          'Credentials in src/services/itraceSerializationService.js (sirimSiteAdmin@gmail.com / SirimSitePasswd123) may have changed. Contact the iTrace team to confirm, then update the constants and redeploy.',
      },
      {
        heading: 'Portal shows "Session Expired"',
        description:
          'Service tokens are single-use. If the portal tab reloads or you come back to an old tab, click "Open iTrace Portal" again in SIRIM — a fresh token is generated each time.',
      },
      {
        heading: 'Plant Batch does not appear in the "New Commission" search',
        description:
          'The batch is already assigned to another commission. Only Available batches show up. Either end another SIRIM cycle to create a new batch, or find the commission that already consumed it.',
      },
      {
        heading: 'Browser blocked the new tab',
        description:
          'Your browser blocked the popup from SIRIM. Allow popups from the SIRIM domain, or copy the portal URL from the Plant Batch confirmation modal and paste it into a new tab manually.',
      },
      {
        heading: 'Commission shows "Labels not yet printed"',
        description:
          'This is expected for freshly created commissions. Labels stay in status CREATED until you click "Print Labels" in the commission detail page. Validation / scanning with a PDA is only available after printing.',
      },
      {
        heading: 'QR Viewer shows "Plant Batch exists but no Commission linked yet"',
        description:
          'Your harvest made it to iTrace as a Plant Batch but nobody has created a Commission for it yet. Click "Open iTrace to create a Commission" inside the viewer, or manually: iTrace portal → Commission Tasks → New Commission → pick this Plant Batch in Batch Source. Once the commission exists, re-open the QR Viewer in SIRIM and the QR codes will render.',
      },
      {
        heading: 'QR Viewer shows "No Plant Batch found for this cycle"',
        description:
          'The cycle was ended before the iTrace integration was active, so no Plant Batch exists. Re-end the cycle from Farm Management (safe to re-run — it will create a fresh Plant Batch in iTrace without touching the Supabase season).',
      },
      {
        heading: 'Print / Save as PDF produces an ugly layout',
        description:
          'Check that you are using the Print button inside the app (top-right on a guide page, or toolbar inside the QR Viewer) — not the browser\'s menu print. The in-app button triggers our print-only CSS that hides the sidebar and chrome. If the browser is still showing the sidebar, refresh the page and try again (the CSS may not have loaded on the first visit).',
      },
    ],
  },

  {
    id: 'mygap-form-1-1',
    title: 'Fill myGAP Form 1-1 (Borang Permohonan)',
    summary: 'Fill the myGAP application form from the dashboard. Cross-device, autosaves, exports as PDF ready to submit.',
    tags: ['mygap', 'form 1', 'form 1-1', 'borang permohonan', 'application', 'doa', 'certification'],
    category: 'myGAP',
    sections: [
      {
        heading: '1. Open the form',
        description:
          'Farm Management → pick a farm → myGAP tab → open the export-mode dropdown (top right) and choose "Form 1-1 (Application)". The editable preview appears below.',
        hook: 'Farm Management → myGAP tab → export dropdown',
      },
      {
        heading: '2. Section A — Applicant (Pemohon)',
        description:
          'In the Quick Fill strip at the top, use the "Pemohon" dropdown to pick an existing employee or the owner. It auto-fills Name, IC, Contact Person, Email, Phone. You can also type any field manually to override.',
        note:
          'The dropdown is sourced from get_farm_employees. To see someone here, add them in the Employees tab first.',
      },
      {
        heading: '3. Section B — Farm (Maklumat Ladang)',
        description:
          'Auto-filled from the currently selected farm: Nama Ladang, Keluasan Fizikal (hectares), Alamat, Poskod, Bandar, Negeri. Pick Jenis Ladang, Ladang Kontrak, Sistem Ladang from the dropdowns.',
      },
      {
        heading: '4. Section C — Crop (Jenis Tanaman)',
        description:
          'In Quick Fill, pick a cycle from the "Tanaman" dropdown. Crop name and Commodity (Buah-buahan / Sayur-sayuran / Padi / Lain-lain) auto-classify from the produce name. Enter Keluasan Bertanam (ha) for that crop.',
      },
      {
        heading: '5. Section D — Marketing (Pemasaran)',
        description:
          'Tick the checkboxes for Direct Sales / Export / Others. If exporting, type the country. If Others, describe the channel.',
      },
      {
        heading: '6. Section E — Declaration (Pengesahan)',
        description:
          'Today\'s date pre-fills. Click "Choose File" to upload a signature image (PNG or JPG). It gets embedded into the exported PDF.',
        note:
          'Sections F (officer) and G (secretariat) are intentionally left blank — those are filled by the DOA officer after submission.',
      },
      {
        heading: '7. Save & Export',
        description:
          'Every edit autosaves to the cloud (Supabase mygap_form11_draft table) so the form is the same on any device you log into. The save indicator shows "Saving…" → "✓ Saved to cloud". Click "Export PDF" to download the final form.',
      },
    ],
  },

  {
    id: 'mygap-form-4',
    title: 'myGAP Form 4 (Farm Record) — auto-fill cheatsheet',
    summary: 'Form 4-1 to 4-8 pulls from your day-to-day farm data: SOP activities, inventory, financial transactions, yields, and employees. Here\'s what comes from where, and how to fill the gaps.',
    tags: ['mygap', 'form 4', 'farm record', 'sop', 'inventory', 'financial', 'auto-fill', 'doa'],
    category: 'myGAP',
    sections: [
      {
        heading: 'Where to find it',
        description:
          'Farm Management → pick a farm → myGAP tab → export dropdown → "Farm Record (4-1 to 4-8)". The eight sections render below as live tables, then "Export PDF" prints the final form.',
        hook: 'Farm Management → myGAP tab → export dropdown → "Farm Record (4-1 to 4-8)"',
      },
      {
        heading: 'How auto-fill works',
        description:
          'Each cell with a dashed-line input is editable. Yellow background = empty, white background = a value is filled (either auto-derived or typed). Auto-derived values come from your existing dashboard data. Type to override at any time — your text always wins.',
      },
      {
        heading: 'Two easy ways to log a SOP activity',
        description:
          '(a) Planting Schedule tab — full SOP builder, good for setting up a cycle\'s schedule up-front. (b) Farm Diary tab → "Add Activity" — appends a single activity (Day, Process, Description, Dosage, Date) directly into the cycle\'s existing SOP. Use this for one-off field events ("sprayed an extra round today, here\'s the note"). Both write to the same sop_record_activity rows that feed Form 4.',
        note:
          'If the cycle does NOT have an SOP yet, "Add Activity" opens the SOP template picker so you can clone a template or build one from scratch — same as before.',
      },
      {
        heading: 'Mark activities as Done (Update Progress)',
        description:
          'A planned activity counts as completed in Form 4 only when its status is "Done" (status = 4). In Farm Diary → "Update Progress", toggle each finished activity to Done and click Save Changes. This also drives the cycle\'s progress %.',
      },
      {
        heading: '4-1 — Pemuliharaan dan Rawatan Tanah / Substrat',
        description:
          'Auto: Tarikh, Kaedah, Bahan, Kuantiti, Lokasi, Operator — from SOP activities (Planting Schedule). "Masalah Tanah" auto-fills from the SOP activity description if you wrote one. "Tempoh Dilarang Menanam" is manual.',
        note:
          'To make this complete: when logging a soil-related SOP activity, write the actual problem in the description (e.g. "pH soil rendah 5.2, perlu lime").',
      },
      {
        heading: '4-2 — Pembelian, Penggunaan dan Rawatan Bahan Tanaman',
        description:
          'Auto: Tarikh, Kuantiti, Harga from Inventory rows where type matches Benih/Seed/Sapling. "Justifikasi" auto-fills from the matching financial-transaction description. Supplier (Nama Pembekal Benih) comes from the financial transaction\'s vendor.',
        note:
          'To make this complete: in Financial → log the seedling purchase with the vendor name and a clear description.',
      },
      {
        heading: '4-3 — Pembelian dan Penggunaan Baja',
        description:
          'Auto: Tarikh, Jenis Bahan, Kuantiti, Harga from Inventory rows where type = Baja/Fertiliser. Pembekal from financial-transaction vendor. "Formulasi" auto-detects NPK patterns ("15-15-15") or Urea %. "Cara Penggunaan" comes from any SOP activity that used the same material — combining tool_name + description.',
        note:
          'To make this complete: (1) Name fertilizer materials with their formulation in the name, e.g. "NPK 15-15-15". (2) Log a SOP activity that uses the material with a tool selected — "Cara Penggunaan" then auto-fills.',
      },
      {
        heading: '4-4 — Pengawasan dan Kawalan Perosak',
        description:
          'Two row sources: SOP pest activities (date, process_name, operator) + Inventory pesticide purchases. "Cara Pengawasan" auto-fills from the SOP description. "Nama Perosak" / "Bil. Perosak / % Kerosakan" only auto-fill if the SOP description uses an explicit format like "Perosak: aphid" or "Kerosakan: 10%" — otherwise they\'re left blank.',
        note:
          'Tip: write SOP pest inspection notes like "Perosak: Helopeltis spp; Kerosakan: 8% on Block A" — then the next form export has the pest name + damage already filled.',
      },
      {
        heading: '4-5 — Pembelian & Penggunaan Racun Perosak',
        description:
          'Auto: Tarikh, Tanaman, Lokasi, Nama Dagangan, Harga, Kuantiti from Inventory rows where type = Racun/Pesticide. "Alat Yang Digunakan" auto-fills from the SOP spray activity\'s tool_name. "Justifikasi" comes from financial-transaction description or SOP description. No. LRMP, PHI, REI are manual.',
        note:
          'To make this complete: when logging a spray SOP activity, pick the correct tool (Knapsack, Boom Sprayer, etc.) — that flows straight into "Alat Yang Digunakan".',
      },
      {
        heading: '4-6 — Pengeluaran dan Penjualan Hasil',
        description:
          'Fully auto from Yield transactions (Harvest tab): Tarikh, Jenis Tanaman, Tuai/Jual kg, Harga, Total, Invois, Pembeli. If the yield row has no buyer_name, Pembeli falls back to the organisation_client linked to the matching financial Sales transaction.',
      },
      {
        heading: '4-7 — Rawatan Pasca Tuai',
        description:
          'Auto: Tarikh, Jenis Tanaman, Lokasi, Jenis Rawatan, Operator — from SOP activities whose process matches "pasca tuai / post-harvest / pembungkusan / grading". "Justifikasi" auto-fills from SOP description. "Bahan Aktif dan Kadar" combines name_material + dosage_amount + dosage_unit_symbol. Tempoh Rawatan stays manual.',
      },
      {
        heading: '4-8 — Pengurusan Pekerja',
        description:
          'Auto: Nama, Tarikh Lahir, IC, Jantina, Warganegara from Employees. Training Date / Name / Organiser from the training_records table. Health Records (Tarikh, Klinik / Hospital, Nota) are manual — there\'s no DB table for them yet.',
        note:
          'To make this complete: add each employee with full details (IC, birthdate, gender) in the Employees tab.',
      },
      {
        heading: 'Checklist to maximize auto-fill before exporting',
        description:
          '(1) Log every SOP activity — either Planting Schedule (full editor) or Farm Diary → Add Activity (single-row append). Write a clear description, pick the right process, and set material/tool/dosage. (2) Farm Diary → Update Progress → mark finished activities as Done so they count as completed. (3) Inventory → record purchases with the right material type (Benih / Baja / Racun). (4) Financial → add the receipt with vendor + description. (5) Harvest → record yields with buyer_name. (6) Employees → fill IC, birthdate, gender. (7) Pick the correct cycle in the cycle dropdown before exporting.',
      },
    ],
  },
];

export default itraceGuides;
